(() => {
  'use strict';

  const ELEMENT_NAME = 'hero-split';
  const MIN_INTERVAL_MS = 4000;
  const DEFAULT_INTERVAL_MS = 7000;
  const SWIPE_THRESHOLD_PX = 40;
  const SWIPE_MAX_VERTICAL_RATIO = 0.75;

  if (customElements.get(ELEMENT_NAME)) return;

  class HeroSplit extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;

      this.slides = [...this.querySelectorAll('[data-hero-slide]')];
      if (!this.slides.length) {
        console.warn(`<${ELEMENT_NAME}>: no slides found, carousel not initialised.`);
        return;
      }

      this.controller = new AbortController();
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.track = this.querySelector('[data-hero-track]');
      this.dots = [...this.querySelectorAll('[data-hero-dot]')];
      this.status = this.querySelector('[data-hero-status]');
      this.pauseButton = this.querySelector('[data-hero-pause]');
      this.pauseLabel = this.pauseButton?.querySelector('[data-hero-pause-label]');
      this.index = 0;
      this.paused = this.dataset.autoplay !== 'true';
      this.hovered = false;
      this.focused = this.contains(document.activeElement);
      this.editorSelected = false;
      this.pointerStart = null;

      this.bindEvents();
      this.setAttribute('data-hero-ready', '');
      this.show(0);
    }

    disconnectedCallback() {
      window.clearTimeout(this.timer);
      this.controller?.abort();
      this.controller = null;
      this.removeAttribute('data-hero-ready');
      this.slides?.forEach((slide) => {
        slide.removeAttribute('aria-hidden');
        slide.removeAttribute('data-hero-active');
        slide.inert = false;
      });
    }

    get isReducedMotion() {
      return this.motionQuery.matches;
    }

    on(target, event, handler, options = {}) {
      target?.addEventListener(event, handler, { ...options, signal: this.controller.signal });
    }

    bindEvents() {
      this.on(this.querySelector('[data-hero-prev]'), 'click', () => this.show(this.index - 1, true));
      this.on(this.querySelector('[data-hero-next]'), 'click', () => this.show(this.index + 1, true));
      this.dots.forEach((dot, index) => {
        this.on(dot, 'click', (event) => {
          event.preventDefault();
          this.show(index, true);
        });
      });
      this.on(this.pauseButton, 'click', () => {
        this.paused = !this.paused;
        this.updatePlayback();
      });
      this.on(this.querySelector('[data-hero-scroll-next]'), 'click', (event) => this.scrollToNextSection(event));

      this.on(this, 'keydown', (event) => this.onKeydown(event));
      this.on(this, 'mouseenter', () => this.setHovered(true));
      this.on(this, 'mouseleave', () => this.setHovered(false));
      this.on(this, 'focusin', () => {
        this.focused = true;
        this.updatePlayback();
      });
      this.on(this, 'focusout', (event) => {
        this.focused = this.contains(event.relatedTarget);
        this.updatePlayback();
      });

      this.on(this.track, 'pointerdown', (event) => this.onPointerDown(event));
      this.on(this.track, 'pointerup', (event) => this.onPointerUp(event));
      this.on(this.track, 'pointercancel', () => {
        this.pointerStart = null;
      });

      this.on(document, 'visibilitychange', () => this.updatePlayback());
      this.on(this.motionQuery, 'change', () => this.updatePlayback());

      this.on(document, 'shopify:block:select', (event) => {
        const index = this.findSlideIndex(event.detail?.blockId);
        if (index < 0) return;
        this.editorSelected = true;
        this.show(index);
      });
      this.on(document, 'shopify:block:deselect', (event) => {
        if (this.findSlideIndex(event.detail?.blockId) < 0) return;
        this.editorSelected = false;
        this.updatePlayback();
      });
    }

    findSlideIndex(blockId) {
      if (!blockId) return -1;
      return this.slides.findIndex((slide) => slide.dataset.blockId === blockId);
    }

    setHovered(hovered) {
      this.hovered = hovered;
      this.updatePlayback();
    }

    onKeydown(event) {
      if (this.slides.length < 2 || event.target.closest('input, textarea, select')) return;
      if (event.key === 'ArrowLeft') this.show(this.index - 1, true);
      else if (event.key === 'ArrowRight') this.show(this.index + 1, true);
    }

    onPointerDown(event) {
      if (this.slides.length < 2 || (event.pointerType === 'mouse' && event.button !== 0)) return;
      this.pointerStart = { x: event.clientX, y: event.clientY };
    }

    onPointerUp(event) {
      if (!this.pointerStart) return;
      const deltaX = event.clientX - this.pointerStart.x;
      const deltaY = event.clientY - this.pointerStart.y;
      this.pointerStart = null;
      const isHorizontal = Math.abs(deltaY) <= Math.abs(deltaX) * SWIPE_MAX_VERTICAL_RATIO;
      if (Math.abs(deltaX) < SWIPE_THRESHOLD_PX || !isHorizontal) return;
      this.show(this.index + (deltaX < 0 ? 1 : -1), true);
    }

    scrollToNextSection(event) {
      const section = this.closest('.shopify-section');
      const next = section?.nextElementSibling;
      if (!next) return;
      event.preventDefault();
      next.scrollIntoView({ behavior: this.isReducedMotion ? 'auto' : 'smooth', block: 'start' });
    }

    show(nextIndex, announce = false) {
      const total = this.slides.length;
      this.index = ((nextIndex % total) + total) % total;

      this.slides.forEach((slide, index) => {
        const active = index === this.index;
        slide.toggleAttribute('data-hero-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
      });

      this.dots.forEach((dot, index) => {
        if (index === this.index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

      if (announce && this.status) {
        const template = this.dataset.labelSlideStatus || '[current] / [total]';
        this.status.textContent = template.replace('[current]', this.index + 1).replace('[total]', total);
      }

      this.updatePlayback();
    }

    updatePauseButton() {
      if (!this.pauseButton) return;
      const reduced = this.isReducedMotion;
      let label = this.dataset.labelPause;
      let ariaLabel = this.dataset.labelPauseAria;
      if (reduced) {
        label = this.dataset.labelMotionOff;
        ariaLabel = this.dataset.labelMotionOffAria;
      } else if (this.paused) {
        label = this.dataset.labelPlay;
        ariaLabel = this.dataset.labelPlayAria;
      }
      this.pauseButton.disabled = reduced;
      if (this.pauseLabel && label) this.pauseLabel.textContent = label;
      if (ariaLabel) this.pauseButton.setAttribute('aria-label', ariaLabel);
    }

    updatePlayback() {
      window.clearTimeout(this.timer);
      this.updatePauseButton();

      const blocked =
        this.paused ||
        this.isReducedMotion ||
        this.hovered ||
        this.focused ||
        this.editorSelected ||
        document.hidden ||
        this.slides.length < 2;
      if (blocked) return;

      const interval = Math.max(MIN_INTERVAL_MS, Number(this.dataset.interval) || DEFAULT_INTERVAL_MS);
      this.timer = window.setTimeout(() => this.show(this.index + 1), interval);
    }
  }

  customElements.define(ELEMENT_NAME, HeroSplit);
})();
